import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { extractStylesToArray, isSafe } from "@insite/client-framework/Common/Utilities/isSafeStyles";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OfferingModel {
    fields: {
        title: string;
        linkTitle: string;
    };
}

const enum fields {
    offerings = "offerings",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.offerings]: OfferingModel[];
    };
    extendedStyles?: OfferingStyles;
}

export interface OfferingStyles {
    titleText?: TypographyProps;
    offeringTitle: TypographyProps;
    gridWrapper?: GridContainerProps;
    gridItem?: GridItemProps;
    descriptionWrapper?: InjectableCss;
}

export const offeringStyles: OfferingStyles = {
    titleText: {
        variant: "h3",
        transform: "uppercase",
        css: css`
            border-width: 20px;
            text-align: center;
            margin: 0;
            padding: 30px 0;
        `,
    },
    offeringTitle: {
        css: css`
            margin: 0;
            width: 100%;
            margin-bottom: 0.5em;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 24px;
                            line-height: 32px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    gridWrapper: {
        gap: 20,
        css: css`
            flex-wrap: wrap;
            > div[class^="GridOffset"] {
                width: 100%;
                margin: 0;
                justify-content: center;
                > div[class^="GridItemStyle"]:first-child {
                    > div {
                        background: ${COLORS.background.grayShade};
                        border: none;
                        margin-right: 12px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        margin-right: 0;
                                    `,
                                    null,
                                    null,
                                ],
                                "max",
                            )}
                        p {
                            font-weight: 600;
                        }
                    }
                }
                > div[class^="GridItemStyle"]:nth-child(2) {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                css`
                                    border-right: none;
                                `,
                                null,
                            ],
                            "minMax",
                        )}
                }
                > div[class^="GridItemStyle"]:nth-child(3) {
                    @media (min-width: 992px) and (max-width: 1180px) {
                        border-right: none;
                    }
                }
                > div[class^="GridItemStyle"]:nth-child(4) {
                    border-right: none;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                display: block;
                            `,
                            css`
                                display: block;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }
            .mobilehide {
                display: none;
            }
        `,
    },
    gridItem: {
        width: [12, 12, 6, 3, 3],
        css: css`
            align-items: center;
            flex-direction: column;
            padding-right: 0;
            border-right: 2px solid ${getColor("primary")};
            flex-basis: auto;
            max-width: auto;
            padding: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: none;
                            padding: 10px;
                        `,
                        css`
                            border: none;
                            padding: 10px;
                        `,
                        css`
                            margin-bottom: 10px !important;
                        `,
                        css`
                            margin-bottom: 10px !important;
                        `,
                    ],
                    "minMax",
                )}

            @media (min-width: 992px) and (max-width: 1210px) {
                max-width: 33.33% !important;
                flex-basis: 33.33% !important;
            }
        `,
    },
    descriptionWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            padding: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                            border: 2px solid #bdbbbd;
                            margin-right: 0;
                        `,
                        null,
                    ],
                    "max",
                )}
            p {
                font-size: 18px;
                line-height: 28px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                line-height: 24px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }
        `,
    },
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        // pageLink: ownProps.fields.offerings.map(card => getLink(state, card.fields.destination)),
    };
};

const mapDispatchToProps = {};
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const Solutions: React.FC<Props> = ({ fields, extendedStyles }) => {
    const [styles] = useState(() => mergeToNew(offeringStyles, extendedStyles));

    const [windowSize, setWindowSize] = React.useState(0);
    const [isMobile, setIsMobile] = React.useState(false);
    const [offeringData, setOfferingData] = React.useState<OfferingModel[] | undefined>([]);
    const [numberOffering, setNumberOffering] = React.useState(3);

    if (!offeringData) {
        return null;
    }

    useLayoutEffect(() => {
        setWindowSize(window.innerWidth);
        if (windowSize < 768) {
            setIsMobile(true);
        } else {
            setIsMobile(false);
            setNumberOffering(offeringData.length);
        }
    }, [windowSize]);

    useEffect(() => {
        if (fields.offerings?.length) {
            // const data = isMobile ? fields.offerings.filter((item, i) => i < numberOffering) : fields.offerings;
            setOfferingData(fields.offerings);
        }
    }, []);

    return (
        <GridContainer {...styles.gridWrapper}>
            {offeringData?.map((offering, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <>
                    <GridItem {...styles.gridItem}>
                        <StyledWrapper {...styles.descriptionWrapper}>
                            <Typography variant="h4" {...styles.offeringTitle}>
                                {translate("{0}", offering.fields.title)}
                            </Typography>
                            <p>{translate("{0}", offering.fields.linkTitle)}</p>
                        </StyledWrapper>
                    </GridItem>
                </>
            ))}
        </GridContainer>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Solutions),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.offerings,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "linkTitle",
                        defaultValue: "",
                        displayName: "Content",
                        editorTemplate: "RichTextField",
                        validate: (html: string) => {
                            const arrayOfStyles = extractStylesToArray(html);
                            return arrayOfStyles?.every(style => isSafe(style))
                                ? null
                                : "The HTML contains invalid styles that prevent it from being saved";
                        },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
